/*
 * @Author: Alex
 * @Date: 2023-05-10 14:10:41
 * @Description: canvas control base
 */
import { Control } from 'ol/control'
import { Style, Stroke, Fill, Text } from 'ol/style'
import { Map } from 'ol'
import { EventsKey } from 'ol/events'
import { getMapCanvas } from '../mapUtil'
import { unByKey } from 'ol/Observable'
import RenderEvent from 'ol/render/Event'

export interface CanvasControlOptions {
  style?: Style | undefined
  element?: HTMLElement | undefined
}

export abstract class CanvasControlBase extends Control {
  #listener!: EventsKey | null
  private _style!: Style | undefined

  constructor(options: CanvasControlOptions | undefined) {
    options = options || {}
    super(options)
    // Define a style to draw on the canvas
    this.setStyle(options.style)
  }

  protected abstract draw(e: RenderEvent): void

  setMap(map: Map) {
    //
    this.getCanvas(map)

    const oldmap = this.getMap()
    if (this.#listener) {
      unByKey(this.#listener)
      this.#listener = null
    }
    super.setMap(map)
    if (oldmap) {
      try {
        oldmap.renderSync()
      } catch (e) {
        /* ok */
      }
    }

    if (map) {
      this.#listener = map.on('postcompose', e => {
        this.draw(e)
      })
    }
  }

  /** Get canvas overlay
   */
  getCanvas(map: Map) {
    return getMapCanvas(map)
  }

  /** Get map Canvas
   * @private
   */
  getContext(e: any): CanvasRenderingContext2D | null {
    let ctx = e.context
    if (!ctx && this.getMap()) {
      const c = this.getMap()
        ?.getViewport()
        .getElementsByClassName('ol-fixedoverlay')[0]
      ctx = c ? (c as HTMLCanvasElement).getContext('2d') : null
    }
    return ctx
  }

  setStyle(style: Style | undefined) {
    this._style = style || new Style({})
  }

  getStyle() {
    return this._style
  }

  /** Get stroke
   * @api
   */
  getStroke() {
    const t = this._style?.getStroke()
    if (!t) this._style?.setStroke(new Stroke({ color: '#000', width: 1.25 }))
    return this._style?.getStroke()
  }
  /** Get fill
   * @api
   */
  getFill() {
    const t = this._style?.getFill()
    if (!t) this._style?.setFill(new Fill({ color: '#fff' }))
    return this._style?.getFill()
  }

  /** Get stroke
   * @api
   */
  getTextStroke() {
    let t = this._style?.getText()
    if (!t) t = new Text({})
    if (!t.getStroke()) t.setStroke(new Stroke({ color: '#fff', width: 3 }))
    return t.getStroke()
  }
  /** Get text fill
   * @api
   */
  getTextFill() {
    let t = this._style?.getText()
    if (!t) t = new Text({})
    if (!t.getFill()) t.setFill(new Fill({ color: '#fff' }))
    return t.getFill()
  }
  /** Get text font
   * @api
   */
  getTextFont(): string {
    let t = this._style?.getText()
    if (!t) t = new Text({})
    if (!t.getFont()) t.setFont('12px sans-serif')
    return t.getFont() ?? '12px sans-serif'
  }
}
